import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { Input, Switch } from 'formik-antd';
import { useEffect, useState } from 'react';
import { SketchPicker } from 'react-color';
import { useIntl } from 'react-intl';
import IntlMessages from '../../../../../../../domain/utility/IntlMessages';
import FormItem from '../../../../../../../shared/components/form-item';
import LanguagesTab from '../../../../../../../shared/components/languages-tab';
import { ProductAttributeType } from '../../../../../../../shared/constants/AppEnums';
import LanguageCode from '../../../../../../../shared/services/types/language-code';
import ValueModalLanguage from '../value-modal-language';
import { validationInputNumber } from 'src/shared/functions/validation-input-number';
import MainUtils from 'src/shared/utils/main';
import { useLocaleContext } from 'src/domain/utility/AppContextProvider/LocaleContextProvide';

interface ICreateOrEditPredefinedValuePros {
  productAttributeType: string;
}

const CreateOrEditPredefinedValue = (
  props: ICreateOrEditPredefinedValuePros,
) => {
  const { productAttributeType } = props;

  const { messages } = useIntl();

  const [sketchPickerColor, setSketchPickerColor] = useState<any>({
    hex: '#23304C',
  });

  const { setFieldValue, values } = useFormikContext<any>();

  const { locale }: any = useLocaleContext();

  useEffect(() => {
    if (values.colorSquaresRgb) {
      setSketchPickerColor({ hex: `${values.colorSquaresRgb}` });
    } else {
      setSketchPickerColor({
        hex: '#23304C',
      });
    }
  }, [values]);

  const langComponent = [
    {
      code: LanguageCode.English,
      langComponent: (
        <ValueModalLanguage code={LanguageCode.English} index={0} />
      ),
    },
    {
      code: LanguageCode.Español,
      langComponent: (
        <ValueModalLanguage code={LanguageCode.Español} index={1} />
      ),
    },
    {
      code: LanguageCode.Arabic,
      langComponent: (
        <ValueModalLanguage code={LanguageCode.Arabic} index={2} />
      ),
    },
  ];

  return (
    <Row gutter={[0, 20]}>
      <Col span={24}>
        <div className='languages-tab'>
          <LanguagesTab
            languagesList={langComponent}
            tabItem={{
              key: 'Standard',
              label: `${messages['category.categoryInfo.Standard'].toString()}`,
              children: (
                <ValueModalLanguage
                  code={`${!MainUtils.isEmptyValue(locale.code)
                      ? locale.code
                      : LanguageCode.English
                    }`}
                />
              ),
            }}
          />
        </div>
      </Col>

      {productAttributeType === ProductAttributeType.Color && (
        <>
          <Col span={24}>
            <FormItem
              name='colorSquaresRgb'
              label={messages[
                'productAttributes.productAttributesPredefinedValues.color'
              ].toString()}
              fieldCommponent={
                <Input
                  name='colorSquaresRgb'
                  className='width-100'
                  style={{
                    backgroundColor: `${sketchPickerColor?.hex}`,
                    height: 50,
                    color: `#${(
                      Number(`0x1${sketchPickerColor?.hex?.replace('#', '')}`) ^
                      0xffffff
                    )
                      .toString(16)
                      .substr(1)
                      .toUpperCase()}`,
                    borderRadius: '10px',
                  }}
                  value={sketchPickerColor.hex}
                />
              }
            />
          </Col>

          <Col span={18} offset={6} className='mb-20'>
            <SketchPicker
              onChange={(color) => {
                setFieldValue('colorSquaresRgb', color.hex);
                setSketchPickerColor({ hex: color.hex });
              }}
              color={sketchPickerColor.hex}
            />
          </Col>
        </>
      )}

      <Col span={24}>
        <FormItem
          name='priceAdjustment'
          label={messages[
            'productAttributes.productAttributesPredefinedValues.priceAdjustment'
          ].toString()}
          titleTooltip={messages[
            'tooltip.productAttributes.productAttributesPredefinedValues.priceAdjustment'
          ].toString()}
          isTooltip
          fieldCommponent={
            <Input
              name='priceAdjustment'
              onChange={(e) => {
                const value = e.target.value;
                if (validationInputNumber(value)) {
                  setFieldValue('priceAdjustment', Number(value));
                } else setFieldValue('priceAdjustment', 0);
              }}
            />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='cost'
          label={messages[
            'productAttributes.productAttributesPredefinedValues.cost'
          ].toString()}
          titleTooltip={messages[
            'tooltip.productAttributes.productAttributesPredefinedValues.cost'
          ].toString()}
          isTooltip
          fieldCommponent={
            <Input
              name='cost'
              onChange={(e) => {
                const value = e.target.value;
                if (validationInputNumber(value)) {
                  setFieldValue('cost', Number(value));
                } else setFieldValue('cost', 0);
              }}
            />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem
          name={`isPreSelected`}
          label={messages[
            'productAttributes.productAttributesPredefinedValues.isPreSelected'
          ].toString()}
          titleTooltip={messages[
            'tooltip.productAttributes.productAttributesPredefinedValues.isPreSelected'
          ].toString()}
          isTooltip
          fieldCommponent={
            <Switch
              name={`isPreSelected`}
              checkedChildren={<IntlMessages id='common.active' />}
              unCheckedChildren={<IntlMessages id='common.inactive' />}
            />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='displayOrder'
          label={messages[
            'productAttributes.productAttributesPredefinedValues.displayOrder'
          ].toString()}
          titleTooltip={messages[
            'tooltip.productAttributes.productAttributesPredefinedValues.displayOrder'
          ].toString()}
          isTooltip
          fieldCommponent={
            <Input
              name='displayOrder'
              onChange={(e) => {
                const value = e.target.value;
                if (validationInputNumber(value)) {
                  setFieldValue('displayOrder', Number(value));
                } else setFieldValue('displayOrder', 0);
              }}
            />
          }
        />
      </Col>
    </Row>
  );
};

export default CreateOrEditPredefinedValue;
